/**
 * XSS防护工具
 * 用于防止跨站脚本攻击
 */

/**
 * HTML转义映射表
 */
const HTML_ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;'
}

/**
 * HTML转义
 * @param {string} text - 需要转义的文本
 * @returns {string} 转义后的安全文本
 */
export function escapeHtml(text) {
  if (typeof text !== 'string') {
    return text
  }
  return text.replace(/[&<>"'/]/g, (char) => HTML_ESCAPE_MAP[char])
}

/**
 * 转义用户输入的内容（用于显示）
 * @param {string} content - 用户输入的内容
 * @returns {string} 安全的HTML内容
 */
export function sanitizeUserContent(content) {
  if (!content) return ''

  // 1. HTML转义
  let safe = escapeHtml(content)

  // 2. 保留换行符（转换为<br>）
  safe = safe.replace(/\n/g, '<br>')

  return safe
}

/**
 * 验证URL是否安全
 * @param {string} url - 需要验证的URL
 * @returns {boolean} 是否安全
 */
export function isSafeUrl(url) {
  if (!url) return false

  // 只允许http、https、相对路径
  const safeProtocols = /^(https?:\/\/|\/)/i

  // 禁止javascript:、data:、vbscript:等危险协议
  const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i

  if (dangerousProtocols.test(url)) {
    return false
  }

  return safeProtocols.test(url) || !url.includes(':')
}

/**
 * 清理URL（移除危险协议）
 * @param {string} url - 需要清理的URL
 * @returns {string} 安全的URL
 */
export function sanitizeUrl(url) {
  if (!url) return ''

  if (!isSafeUrl(url)) {
    console.warn('检测到不安全的URL:', url)
    return '#'
  }

  return url
}

/**
 * 验证图片URL是否安全
 * @param {string} url - 图片URL
 * @returns {boolean} 是否安全
 */
export function isSafeImageUrl(url) {
  if (!url) return false

  // 只允许内部图片路径或HTTP/HTTPS图片
  const safePatterns = [
    /\/api\/chat\/image\//,    // 内部图片（包含完整URL）
    /^https?:\/\//,            // HTTP/HTTPS图片（OSS可能返回http）
    /^\/uploads\//             // 上传目录
  ]

  return safePatterns.some(pattern => pattern.test(url))
}

/**
 * 清理属性值（用于HTML属性）
 * @param {string} value - 属性值
 * @returns {string} 安全的属性值
 */
export function sanitizeAttribute(value) {
  if (typeof value !== 'string') {
    return value
  }

  // 移除可能的事件处理器
  const dangerous = /on\w+\s*=/gi
  if (dangerous.test(value)) {
    console.warn('检测到危险的属性值:', value)
    return ''
  }

  return escapeHtml(value)
}

/**
 * 清理CSS（防止CSS注入）
 * @param {string} css - CSS内容
 * @returns {string} 安全的CSS
 */
export function sanitizeCss(css) {
  if (typeof css !== 'string') {
    return css
  }

  // 移除危险的CSS
  const dangerous = [
    /javascript:/gi,
    /expression\s*\(/gi,
    /import\s+/gi,
    /@import/gi,
    /behavior\s*:/gi
  ]

  let safe = css
  dangerous.forEach(pattern => {
    safe = safe.replace(pattern, '')
  })

  return safe
}

/**
 * 验证文件名是否安全
 * @param {string} filename - 文件名
 * @returns {boolean} 是否安全
 */
export function isSafeFilename(filename) {
  if (!filename) return false

  // 禁止路径遍历字符
  if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
    return false
  }

  // 禁止特殊字符
  // eslint-disable-next-line no-control-regex
  const dangerous = /[<>:"|?*\x00-\x1f]/
  if (dangerous.test(filename)) {
    return false
  }

  return true
}

/**
 * 清理JSON数据（递归转义所有字符串）
 * @param {any} data - JSON数据
 * @returns {any} 清理后的数据
 */
export function sanitizeJson(data) {
  if (typeof data === 'string') {
    return escapeHtml(data)
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeJson(item))
  }

  if (data && typeof data === 'object') {
    const cleaned = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        cleaned[key] = sanitizeJson(data[key])
      }
    }
    return cleaned
  }

  return data
}

/**
 * Vue指令：v-safe-html
 * 用法：<div v-safe-html="userContent"></div>
 */
export const vSafeHtml = {
  mounted(el, binding) {
    el.innerHTML = sanitizeUserContent(binding.value)
  },
  updated(el, binding) {
    el.innerHTML = sanitizeUserContent(binding.value)
  }
}

/**
 * Vue指令：v-safe-text
 * 用法：<div v-safe-text="userInput"></div>
 */
export const vSafeText = {
  mounted(el, binding) {
    el.textContent = binding.value
  },
  updated(el, binding) {
    el.textContent = binding.value
  }
}
